import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight, Building, Loader2, MapPin, Package, Boxes, School as SchoolIcon,
  Coins, Landmark, Search, Info, RefreshCw,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip,
  CartesianGrid, PieChart, Pie, Cell,
} from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import Sidebar from '../components/Sidebar';
import { getSupplierDetails } from '../api';

const numFmt = (n) => (n == null ? 'غير متوفر' : Number(n).toLocaleString('en-US'));
const moneyFmt = (n) => (n == null ? 'غير متوفر' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));
const pctFmt = (n) => (n == null ? 'غير متوفر' : `${Number(n).toFixed(1)}%`);

/**
 * Supplier 360° — dedicated mini command centre for a single supplier.
 * Route: /suppliers/:name  Payload: GET /api/supply/supplier/:name
 */
export default function SupplierDetailPage() {
  const nav = useNavigate();
  const { name } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await getSupplierDetails(name)); }
    catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [name]);
  useEffect(() => { load(); }, [load]);

  const filteredSchools = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = data?.schools || [];
    if (!q) return list;
    return list.filter((s) =>
      (s.school_name || '').toLowerCase().includes(q) ||
      (s.school_id || '').toString().includes(q) ||
      (s.region || '').toLowerCase().includes(q) ||
      (s.governorate || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  if (loading) {
    return (
      <div dir="rtl" className="supply-theme min-h-screen flex">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 size={26} className="animate-spin" style={{ color: '#9D8CFF' }} />
        </main>
      </div>
    );
  }
  if (error || !data?.found) {
    return (
      <div dir="rtl" className="supply-theme min-h-screen flex">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center p-8">
          <div className="se-panel p-6 max-w-md text-center">
            <div className="font-bold mb-2" style={{ color: '#F1EEFB' }}>{error ? 'خطأ' : 'مورد غير موجود'}</div>
            <div className="text-slate-400 text-[12px] mb-4">{error || `لم يتم العثور على بيانات للمورد "${name}"`}</div>
            <button className="se-btn se-btn-primary" onClick={() => nav('/suppliers-list')}>العودة للموردين</button>
          </div>
        </main>
      </div>
    );
  }

  const s = data.summary;

  return (
    <div dir="rtl" className="supply-theme min-h-screen flex">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 md:p-8" data-testid="supplier-360">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-[11px] font-semibold tracking-widest" style={{ color: 'var(--se-violet-2)' }}>SUPPLIER 360°</div>
            <h1 className="se-title mt-1">{data.name}</h1>
            <div className="se-subtitle">لوحة قيادة مخصصة للمورد · {numFmt(s.schools)} مدرسة · {numFmt(s.items)} صنف · {numFmt(s.records)} سجل</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="se-btn se-btn-ghost"><RefreshCw size={13} /> تحديث</button>
            <button onClick={() => nav('/suppliers-list')} className="se-btn se-btn-ghost"><ArrowRight size={13} /> العودة للموردين</button>
          </div>
        </div>

        {/* KPI row */}
        <section className="mb-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi hero label="عدد المدارس" value={numFmt(s.schools)} unit="مدرسة" icon={SchoolIcon} testid="s360-schools" />
            <Kpi label="عدد الأصناف" value={numFmt(s.items)} unit="صنف" icon={Boxes} tint="#38D6C7" testid="s360-items" />
            <Kpi label="الكمية المخصصة" value={numFmt(s.allocated_qty)} unit="قطعة" icon={Package} tint="#9D8CFF" testid="s360-qty" />
            <Kpi label="القيمة" value={moneyFmt(s.allocated_value)} unit="ريال" icon={Coins} tint="#38D6C7" testid="s360-value" />
            <Kpi label="إدارات التعليم" value={numFmt(s.depts)} unit="إدارة" icon={Landmark} tint="#B7ADFF" testid="s360-depts" />
            <Kpi label="المدن" value={numFmt(s.cities)} unit="مدينة" icon={MapPin} tint="#9D8CFF" testid="s360-cities" />
          </div>
          {data.hasDeliveryData && s.completion != null && (
            <div className="mt-3 se-panel p-4 flex items-center gap-4" data-testid="s360-delivery">
              <div className="text-[12.5px] font-semibold" style={{ color: '#F1EEFB' }}>إنجاز التوريد الفعلي</div>
              <div className="flex-1 se-progress"><div style={{ width: `${s.completion}%` }} /></div>
              <div className="text-[16px] font-extrabold tabular-nums" style={{ color: '#34D399' }}>{pctFmt(s.completion)}</div>
              <div className="text-[11px] text-slate-400">{numFmt(s.delivered_qty)} / {numFmt(s.allocated_qty)} قطعة</div>
            </div>
          )}
        </section>

        {/* Analytics row: by dept + by city */}
        <section className="mb-6 grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-6">
            <div className="se-panel p-4">
              <div className="se-section-head mb-3">التخصيص حسب إدارة التعليم</div>
              {data.byDepartment.length === 0 ? <EmptyBox /> : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.byDepartment.slice(0, 10)} layout="vertical" margin={{ right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis type="number" tick={{ fill: '#8F8AA6', fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" width={130} tick={{ fill: '#C7C3DB', fontSize: 11 }} />
                    <RTooltip contentStyle={{ background: '#1B1A25', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, direction: 'rtl', color: '#F1EEFB' }} />
                    <Bar dataKey="qty" name="مخصص" fill="#6D5DFB" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className="col-span-12 lg:col-span-6">
            <div className="se-panel p-4">
              <div className="se-section-head mb-3">التخصيص حسب المدينة</div>
              {data.byCity.length === 0 ? <EmptyBox /> : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.byCity.slice(0, 12)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="name" tick={{ fill: '#8F8AA6', fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={70} />
                    <YAxis tick={{ fill: '#8F8AA6', fontSize: 11 }} />
                    <RTooltip contentStyle={{ background: '#1B1A25', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, direction: 'rtl', color: '#F1EEFB' }} />
                    <Bar dataKey="qty" name="مخصص" fill="#38D6C7" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </section>

        {/* Map + Items side by side */}
        <section className="mb-6 grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-7">
            <div className="se-panel p-3" style={{ height: 420 }}>
              <div className="text-[13px] font-bold mb-2 px-2 pt-1" style={{ color: '#F1EEFB' }}>خريطة توزيع المورد ({data.map.length} مدرسة)</div>
              {data.map.length === 0 ? (
                <div className="se-empty h-full flex items-center justify-center">لا توجد إحداثيات صالحة لهذا المورد</div>
              ) : (
                <MapContainer center={[data.map[0].lat, data.map[0].lng]} zoom={7} scrollWheelZoom
                  style={{ height: 'calc(100% - 32px)', width: '100%', borderRadius: 10, background: '#0F0E15' }}>
                  <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" attribution='&copy; OpenStreetMap' />
                  {data.map.map((m) => (
                    <CircleMarker key={m.school_id} center={[m.lat, m.lng]} radius={5}
                      pathOptions={{ color: '#9D8CFF', fillColor: '#9D8CFF', fillOpacity: 0.7, weight: 1.5 }}>
                      <Popup>
                        <div dir="rtl" style={{ minWidth: 200 }}>
                          <div style={{ fontWeight: 700 }}>{m.school_name || m.school_id}</div>
                          <div style={{ fontSize: 11, color: '#64748B' }}>{m.school_id}</div>
                          <div style={{ fontSize: 11, marginTop: 4 }}>{numFmt(m.items_count)} صنف · {numFmt(m.allocated_qty)} قطعة</div>
                        </div>
                      </Popup>
                    </CircleMarker>
                  ))}
                </MapContainer>
              )}
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <div className="se-panel p-4">
              <div className="se-section-head mb-3">أصناف المورد ({data.items.length})</div>
              <div className="overflow-x-auto max-h-[370px] overflow-y-auto">
                <table className="se-table">
                  <thead><tr><th>الصنف</th><th className="text-center">الكمية</th><th className="text-center">القيمة</th><th className="text-center">مدارس</th></tr></thead>
                  <tbody>
                    {data.items.slice(0, 100).map((it, i) => (
                      <tr key={i} data-testid={`s360-item-${i}`}>
                        <td className="truncate max-w-[220px]" title={it.name}>{it.name}</td>
                        <td className="text-center tabular-nums">{numFmt(it.qty)}</td>
                        <td className="text-center tabular-nums">{moneyFmt(it.value)}</td>
                        <td className="text-center tabular-nums">{numFmt(it.schools)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Schools table */}
        <section className="mb-6">
          <div className="flex items-end justify-between mb-3">
            <div className="se-section-head">مدارس المورد ({data.schools.length})</div>
            <div className="relative">
              <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="se-input w-64 pr-8" placeholder="بحث بالاسم أو الرقم"
                value={search} onChange={(e) => setSearch(e.target.value)} data-testid="s360-search" />
            </div>
          </div>
          <div className="se-panel overflow-x-auto">
            <table className="se-table">
              <thead>
                <tr>
                  <th>اسم المدرسة</th>
                  <th>الرقم الوزاري</th>
                  <th>إدارة التعليم</th>
                  <th>المدينة</th>
                  <th>المرحلة</th>
                  <th className="text-center">الأصناف</th>
                  <th className="text-center">الكمية</th>
                  <th className="text-center">القيمة</th>
                </tr>
              </thead>
              <tbody>
                {filteredSchools.slice(0, 200).map((sch, i) => (
                  <tr key={i} data-testid={`s360-school-${i}`}>
                    <td className="font-semibold">{sch.school_name || 'غير متوفر'}</td>
                    <td className="tabular-nums text-slate-400">{sch.school_id || '—'}</td>
                    <td className="text-slate-400">{sch.governorate || '—'}</td>
                    <td className="text-slate-400">{sch.region || '—'}</td>
                    <td className="text-slate-400">{sch.stage || '—'}</td>
                    <td className="text-center tabular-nums">{numFmt(sch.items_count)}</td>
                    <td className="text-center tabular-nums">{numFmt(sch.allocated_qty)}</td>
                    <td className="text-center tabular-nums">{moneyFmt(sch.allocated_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredSchools.length > 200 && (
            <div className="text-center text-slate-500 text-[11px] mt-2">
              عرض 200 من {numFmt(filteredSchools.length)} مدرسة
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function Kpi({ label, value, unit, icon: Icon, tint, hero, testid }) {
  return (
    <div className={`se-kpi ${hero ? 'se-kpi-hero' : ''}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="se-kpi-label">{label}</div>
        {Icon && (
          <div className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: hero ? 'rgba(255,255,255,0.15)' : `${tint || '#6D5DFB'}22`,
                     border: hero ? '1px solid rgba(255,255,255,0.22)' : `1px solid ${tint || '#6D5DFB'}44` }}>
            <Icon size={15} style={{ color: hero ? '#FFF' : (tint || '#9D8CFF') }} />
          </div>
        )}
      </div>
      <div className="se-kpi-value">{value}</div>
      {unit && <div className="se-kpi-unit">{unit}</div>}
    </div>
  );
}

function EmptyBox() {
  return <div className="se-empty py-10">لا توجد بيانات</div>;
}
